import WellService from 'business/services/WellService';
import { FmlxButton } from 'fmlx-common-ui';
import React, { ChangeEvent, useCallback, useEffect, useRef } from 'react';
import { IStyle } from 'Styles';
import ImagesRenderer from './imagesRenderer';
import ScoringOptionSelector from './ScoringOptionSelector';
import {
  ImageScoringSortState,
  useImageScoringSortContext,
  useIsFetchingData,
  useSetimagesList,
  withImageScoringSortContext
} from './withImageScoringSortContext';

interface Props {
  projectNodeId: number;
  handleSelectionChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  scoringOptionSelectedState: ImageWellScoresAutoScore;
}

const initialStateDefault: ImageScoringSortState = {
  imagesList: [],
  isFetchingData: false,
  selectedImageIndex: 0,
  searchedAlready: false,
};
export type ImageWellScoresAutoScore = {
  scoreGroup: number;
  imageSettings: number;
  searchAutomatically: boolean;
  numberOfResultsToshow: number;
};

const wellService = new WellService();

const SortScoringWellImages = (props: Props) => {
  const { projectNodeId, scoringOptionSelectedState, handleSelectionChange } = props;
  const setImagesList = useSetimagesList();
  const isFetchingData = useIsFetchingData();
  const { setStateDirectly } = useImageScoringSortContext();

  const abortController = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (signal: AbortSignal) => {
      try {
        setImagesList([], true); // clear + loading

        const data = await wellService.getHighScoreWellImagesWithScoreByContainerNodeId(
          projectNodeId,
          scoringOptionSelectedState.scoreGroup,
          scoringOptionSelectedState.imageSettings,
          signal,
        );

        setImagesList(data, false);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.error("Fetch failed:", {err});
          setStateDirectly(initialStateDefault);
        }
      }
    },
    [projectNodeId, scoringOptionSelectedState.scoreGroup, scoringOptionSelectedState.imageSettings],
  );

  const showImagesClicked = useCallback(() => {
    abortController.current?.abort(); // cancel any in-progress
    abortController.current = new AbortController();
    fetchData(abortController.current.signal);
  }, [fetchData]);

  useEffect(() => {
    abortController.current?.abort(); // cancel old one
    abortController.current = new AbortController();
    let timeoutHandle: ReturnType<typeof setTimeout> | null;

    if (scoringOptionSelectedState.searchAutomatically) {
      // make user aware if the process is recreated
      timeoutHandle = setTimeout(()=> {
        fetchData(abortController.current.signal);
      }, 100);
    }

    return () => {
      abortController.current?.abort();
      setStateDirectly(initialStateDefault);
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    };
  }, [
    projectNodeId,
    scoringOptionSelectedState.scoreGroup,
    scoringOptionSelectedState.imageSettings,
    scoringOptionSelectedState.numberOfResultsToshow,
    scoringOptionSelectedState.searchAutomatically,
    fetchData,
  ]);
  
  return (
    <div id="sortscoringwell-images-container" className="p-3 non-draggable">
      <div id="sortscoringwell-images-description" className="mb-5">
        Show top {scoringOptionSelectedState.numberOfResultsToshow} images by score
      </div>
      <ScoringOptionSelector
        scoringOptionSelectedState={scoringOptionSelectedState}
        handleSelectionChange={handleSelectionChange}
      />
      {!scoringOptionSelectedState.searchAutomatically && (
        <div id='sortscoringwellimages-button-wrapper' className="flex justify-end">
          <FmlxButton
            id="sortscoringwellimages-showimages-button"
            type={FmlxButton.Type.BASIC}
            label="Show Images"
            onClick={showImagesClicked}
            disabled={isFetchingData}
            size={FmlxButton.Size.SMALL}
          />
        </div>
      )}
      <ImagesRenderer />
    </div>
  );
};

const styles: IStyle = {
  searchButton: {
    background: '#313131',
    height: '26px',
    padding: '5px',
    borderRadius: '4px',
    color: 'white',
  },
};

export default withImageScoringSortContext(SortScoringWellImages, initialStateDefault);
